/**
 * Utilities for converting React Native style objects into web CSS.
 *
 * React Native styles are plain objects with camelCased properties and numeric
 * values expressed in density-independent pixels. On the web we render real DOM
 * elements, so numeric values become `px` (except for known unitless
 * properties) and a few RN-specific properties are normalised.
 */
import type { CSSProperties } from 'react';

export type RNStyle = CSSProperties & Record<string, any>;
export type StyleProp = RNStyle | false | null | undefined | StyleProp[];

// Properties whose numeric value must NOT be suffixed with `px`.
const UNITLESS = new Set([
  'flex',
  'flexGrow',
  'flexShrink',
  'opacity',
  'zIndex',
  'fontWeight',
  'lineHeight', // RN treats lineHeight as px, but unitless is a safer web default when small
  'aspectRatio',
  'shadowOpacity',
  'elevation',
  'order',
]);

function normaliseValue(key: string, value: unknown): unknown {
  if (typeof value === 'number' && !UNITLESS.has(key)) {
    return `${value}px`;
  }
  return value;
}

/** Recursively flattens an RN style prop (which may be nested arrays) into one object. */
export function flattenStyle(style: StyleProp): RNStyle {
  if (!style) {
    return {};
  }
  if (Array.isArray(style)) {
    return style.reduce<RNStyle>((acc, s) => Object.assign(acc, flattenStyle(s)), {});
  }
  return style as RNStyle;
}

/** Converts a (possibly nested) RN style prop into a web CSS style object. */
export function toCss(style: StyleProp): CSSProperties {
  const flat = flattenStyle(style);
  const out: Record<string, unknown> = {};

  for (const [key, rawValue] of Object.entries(flat)) {
    if (rawValue === undefined || rawValue === null) {
      continue;
    }

    switch (key) {
      // RN shadow props → best-effort web box-shadow.
      case 'shadowColor':
      case 'shadowOffset':
      case 'shadowOpacity':
      case 'shadowRadius':
      case 'elevation': {
        const s = flat as any;
        const color = s.shadowColor ?? 'rgba(0,0,0,0.2)';
        const offset = s.shadowOffset ?? { width: 0, height: s.elevation ? s.elevation / 2 : 1 };
        const radius = s.shadowRadius ?? s.elevation ?? 2;
        out.boxShadow = `${offset.width ?? 0}px ${offset.height ?? 0}px ${radius}px ${color}`;
        break;
      }
      case 'textAlignVertical':
        // Closest web analogue.
        out.verticalAlign = rawValue;
        break;
      case 'includeFontPadding':
        break; // no web equivalent
      default:
        out[key] = normaliseValue(key, rawValue);
    }
  }

  return out as CSSProperties;
}

/** Base flexbox defaults that make DOM elements behave like RN `View`s. */
export const viewBase: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignContent: 'flex-start',
  flexShrink: 0,
  position: 'relative',
  boxSizing: 'border-box',
  minHeight: 0,
  minWidth: 0,
};

// Attributes that are always safe to forward to a DOM element.
const DOM_ATTR_ALLOWLIST = new Set([
  'id',
  'className',
  'role',
  'title',
  'tabIndex',
  'htmlFor',
  'dir',
  'lang',
  'hidden',
  'draggable',
  'placeholder',
  'name',
  'href',
  'src',
  'alt',
  'width',
  'height',
  'colSpan',
  'rowSpan',
  'contentEditable',
  'accessKey',
  'slot',
]);

/**
 * Filters an arbitrary props object down to props that are safe to spread onto
 * a DOM element. styled-components forwards all props (including RN-specific
 * ones like `disabled` or `isSmallerScreen`) to our primitives; without this
 * filter React warns about unknown DOM attributes.
 */
export function filterDomProps(props: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const key in props) {
    if (
      key.startsWith('data-') ||
      key.startsWith('aria-') ||
      DOM_ATTR_ALLOWLIST.has(key) ||
      (key.startsWith('on') && key.length > 2 && key[2] >= 'A' && key[2] <= 'Z')
    ) {
      out[key] = props[key];
    }
  }
  return out;
}
